import { Box, Card, styled, Typography } from "@schaeffler-commerce/design-system";
import { RouterLink } from "~/common/components/Link";
import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import { isSiteRelativeUrl, resolveUrl } from "~/utils/url/urlResolver";

export const CMS_IMAGE_TILE_TYPE = "ImageTileComponent" as const;

export interface ImageTileMediaDTO {
  url?: string;
  altText?: string;
}

export interface ImageTileLinkDTO {
  url?: string;
  labelText?: string;
  target?: string;
}

export interface CMSImageTileViewProps {
  className?: string;
  headerText?: string;
  content?: string;
  imageLink?: string;
  isFullWidth?: boolean;
  media?: ImageTileMediaDTO;
  link?: ImageTileLinkDTO;
  gaCategory?: string;
  gaSubCategory?: string;
}

export interface CMSImageTileProps extends CMSImageTileViewProps {
  type: ComponentTypes.CMSImageTile;
  id: string;
}

export const CMSImageTile = (props: CMSImageTileProps) => <CMSImageTileView {...props} />;

export const CMSImageTileView = ({
  headerText,
  content,
  imageLink,
  isFullWidth,
  media,
  link,
  gaCategory,
  gaSubCategory,
  className,
}: CMSImageTileViewProps) => {
  const imageUrl = media?.url ?? imageLink;
  const linkHref = link?.url;
  const cardClassName = classNames("scha__cms", "scha__cms-image-tile", "js-jsp__tile-component", className);

  if (!imageUrl && !headerText && !content) {
    return null;
  }

  const contents = (
    <>
      {imageUrl ? (
        <TileImageFrame>
          <TileImage src={resolveUrl(imageUrl)} alt={media?.altText ?? ""} $fullWidth={Boolean(isFullWidth)} />
        </TileImageFrame>
      ) : null}
      {headerText || content ? (
        <TileCopy>
          {headerText ? (
            <TileHeading variant="titleMedium" component="h3">
              {headerText}
            </TileHeading>
          ) : null}
          {content ? (
            <Typography variant="bodyMedium" component="p" sx={{ color: "text.secondary" }}>
              {content}
            </Typography>
          ) : null}
        </TileCopy>
      ) : null}
    </>
  );

  const analyticsProps = {
    "data-category": gaCategory,
    "data-sub-category": gaSubCategory,
  };

  return linkHref ? (
    isSiteRelativeUrl(linkHref) && !resolveLinkTarget(link?.target) ? (
      <TileCard
        component={RouterLink}
        to={linkHref}
        aria-label={link?.labelText}
        className={cardClassName}
        {...analyticsProps}
      >
        {contents}
      </TileCard>
    ) : (
      <TileCard
        component="a"
        href={resolveUrl(linkHref)}
        target={resolveLinkTarget(link?.target)}
        aria-label={link?.labelText}
        className={cardClassName}
        {...analyticsProps}
      >
        {contents}
      </TileCard>
    )
  ) : (
    <TileCard component="div" className={cardClassName} {...analyticsProps}>
      {contents}
    </TileCard>
  );
};

const resolveLinkTarget = (target?: string) => (target === "NEWWINDOW" ? "_blank" : undefined);

const TileCard = styled(Card)(({ theme }) => ({
  display: "block",
  height: "100%",
  borderRadius: theme.spacing(1),
  color: theme.palette.text.primary,
  textDecoration: "none",
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shortest,
  }),
  "&[href]": {
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
  },
})) as typeof Card;

const TileImageFrame = styled(Box)(({ theme }) => ({
  aspectRatio: "16 / 9",
  display: "grid",
  placeItems: "center",
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
}));

const TileImage = styled("img", { shouldForwardProp: (prop) => prop !== "$fullWidth" })<{
  $fullWidth: boolean;
}>(({ $fullWidth }) => ({
  display: "block",
  width: $fullWidth ? "100%" : "auto",
  height: $fullWidth ? "100%" : "auto",
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: $fullWidth ? "cover" : "contain",
}));

const TileCopy = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  [theme.breakpoints.up("desktop-s")]: {
    padding: theme.spacing(2.5, 2),
  },
}));

const TileHeading = styled(Typography)(({ theme }) => ({
  ".scha__product-group-page__content &": {
    [theme.breakpoints.down("desktop-s")]: {
      fontWeight: "bold",
    },
  },
})) as typeof Typography;
